import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { Button as CustomButton } from "components"
import { IShiftSwapRequest } from "../interface/shiftData"
import { formatDateNumeric } from "../utils"
import check from "../../../../assets/illustrations/check.svg"
import uncheck from "../../../../assets/illustrations/un-check.svg"
import { XMarkIcon } from "@heroicons/react/20/solid"
import { Divider } from "antd"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "react-toastify"
import { ewaAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"

interface SwapRequestDetailsProps {
  open: boolean
  onClose: () => void
  swap: IShiftSwapRequest | null
  perspective: "sender" | "receiver"
}

export const SwapRequestDetails = ({
  open,
  onClose,
  swap,
  perspective,
}: SwapRequestDetailsProps) => {
  const isSender = perspective === "sender"
  const transferring = isSender
    ? swap?.sourceShiftAssignment
    : swap?.targetShiftAssignment
  const receiving = isSender
    ? swap?.targetShiftAssignment
    : swap?.sourceShiftAssignment
  const transferringDate = isSender
    ? swap?.sourceSwapDate
    : swap?.targetSwapDate
  const receivingDate = isSender ? swap?.targetSwapDate : swap?.sourceSwapDate
  const requiresAdminApproval =
    swap?.sourceShiftAssignment?.shiftPlan?.requireApprovalForSwap
  const isSwap = swap?.targetShiftAssignmentId !== null
  const isRejected = swap?.status === "declined"
  const colleagueRejected = isRejected && !swap?.targetEmployeeApproved
  const adminRejected = isRejected && !colleagueRejected

  const queryClient = useQueryClient()

  const stages = [
    { label: "Create request" },
    { label: "Colleague's approval" },
    ...(requiresAdminApproval ? [{ label: "Admin's approval" }] : []),
  ]

  const getStageState = (i: number): "done" | "declined" | "pending" => {
    if (i === 0) return "done"
    if (i === 1) {
      if (swap?.targetEmployeeApproved) return "done"
      if (colleagueRejected) return "declined"
      return "pending"
    }
    if (i === 2) {
      if (swap?.status === "approved") return "done"
      if (adminRejected) return "declined"
      return "pending"
    }
    return "pending"
  }

  const { mutate: deleteSwap, isLoading: isLoadingDelete } = useMutation({
    mutationFn: async () => {
      return await ewaAxiosInstance.delete(
        `/v1/time-attendance/shift-swaps/${swap?.id}`
      )
    },
    onSuccess: (data: any) => {
      const res = data?.data
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: [
            "/v1/time-attendance/shift-swaps/requests/requested-by-employee",
          ],
        })
        res.message && toast.success(res.message)
        onClose()
      }
    },
    onError: onQueryError,
  })

  return (
    <CustomModal
      rootClassName="ta-scope"
      open={open}
      onCancel={onClose}
      title="Swap request details"
      footer={
        <div className="flex gap-3">
          {isSender && swap?.status === "pending" && (
            <CustomButton
              color="secondary"
              style={{ width: "100%" }}
              submitting={isLoadingDelete}
              disabled={isLoadingDelete}
              onClick={() => deleteSwap()}
            >
              Cancel request
            </CustomButton>
          )}
          <CustomButton
            color="primary"
            style={{ width: "100%" }}
            onClick={onClose}
          >
            Close
          </CustomButton>
        </div>
      }
    >
      <div>
        <p className="mb-4">
          {isSender ? (
            <>
              <span className="font-bold">You</span>{" "}
              <span className="text-[#62748E]">to </span>
              <span className="font-bold">{swap?.targetEmployee.fullName}</span>
            </>
          ) : (
            <>
              <span className="font-bold">{swap?.requester.fullName}</span>{" "}
              <span className="text-[#62748E]">to </span>
              <span className="font-bold">You</span>
            </>
          )}
        </p>

        {(isSender || isSwap) && (
          <div className="mb-4">
            <p className="mb-1 text-[#62748E]">Shift you're transferring</p>
            <p>
              <span className="font-bold">
                {transferring?.shiftPeriod?.title}
              </span>{" "}
              in{" "}
              <span className="font-bold">
                {transferring?.shiftPlan?.title}
              </span>{" "}
              on{" "}
              <span className="font-bold">
                {formatDateNumeric(transferringDate ?? null)}
              </span>
            </p>
          </div>
        )}

        {(!isSender || isSwap) && (
          <div className="mb-4">
            <p className="mb-1 text-[#62748E]">Shift you're receiving</p>
            <p>
              <span className="font-bold">{receiving?.shiftPeriod?.title}</span>{" "}
              in{" "}
              <span className="font-bold">{receiving?.shiftPlan?.title}</span>{" "}
              on{" "}
              <span className="font-bold">
                {formatDateNumeric(receivingDate ?? null)}
              </span>
            </p>
          </div>
        )}
        <Divider style={{ margin: "10px 0", borderTopWidth: "2px" }} />

        {swap?.targetEmployeeReason && (
          <>
            <div className="mb-4">
              <p className=" mb-1 text-[#62748E]">Colleague's reason</p>
              <p className="text-sm text-[#0F172B]">
                {swap.targetEmployeeReason}
              </p>
            </div>

            <Divider style={{ margin: "10px 0", borderTopWidth: "2px" }} />
          </>
        )}
        <div className="">
          <p className="font-bold mb-3">Progress</p>
          <div className="flex items-center gap-2 w-full">
            {stages.map((stage, i) => {
              const state = getStageState(i)
              const barColor =
                state === "done"
                  ? "bg-[#004AF5]"
                  : state === "declined"
                  ? "bg-[#E7000B]"
                  : "bg-[#E2E8F0]"
              return (
                <div
                  key={i}
                  className="flex flex-col items-center gap-1 flex-1"
                >
                  <div className="flex items-center gap-1 w-full">
                    <div className="flex-1 h-1 rounded-full bg-gray-200 overflow-hidden">
                      <div className={`h-full ${barColor} w-full`} />
                    </div>
                    <span className="flex items-center justify-center w-[10px] h-[10px]">
                      {state === "done" ? (
                        <img
                          src={check}
                          alt="done"
                          className="w-[10px] h-[10px]"
                        />
                      ) : state === "declined" ? (
                        <XMarkIcon className="w-3 h-3 text-red-500" />
                      ) : (
                        <img
                          src={uncheck}
                          alt="pending"
                          className="w-[10px] h-[10px]"
                        />
                      )}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 text-center">
                    {stage.label}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </CustomModal>
  )
}
